import { Autocomplete } from '@chiroup/components';
import { FormError } from '@chiroup/core';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useMemo, useState } from 'react';
import Datepicker from 'react-tailwindcss-datepicker';
import { FieldErrors } from './FieldErrors';

dayjs.extend(utc);
dayjs.extend(tz);

const sortTimeIncrements = (
  timeArray: {
    text: string;
    value: string;
  }[],
) => {
  // Define a function to convert time strings to minutes since midnight
  function timeToMinutes(time: string) {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Sort the timeArray based on the minutes since midnight
  return timeArray.sort((time1, time2) => {
    const minutes1 = timeToMinutes(time1.value);
    const minutes2 = timeToMinutes(time2.value);

    // Ensure that times before 6am show up after 11:45pm in the list...
    if (minutes1 < 360) {
      return minutes1 + 1440 - minutes2;
    }
    if (minutes2 < 360) {
      return minutes1 - (minutes2 + 1440);
    }

    return minutes1 - minutes2;
  });
};

type TimeOption = {
  text: string;
  value: string;
};

function roundToNearestInterval(
  date: dayjs.Dayjs,
  interval: number,
): dayjs.Dayjs {
  const minutes = date.minute();
  const remainder = minutes % interval;
  const nextInterval = interval - remainder;

  return date.add(nextInterval, 'minute').startOf('minute');
}

export const getNearest = (timezone: string) => {
  const now = dayjs().tz(timezone);
  const roundedNow = roundToNearestInterval(now, 15);
  const roundedNowUnix = roundedNow.valueOf();
  return roundedNowUnix;
};

export function generateTimeOptions(
  startTime: string,
  endTime: string,
  interval: number,
): TimeOption[] {
  function roundToNearestInterval(time: Date, interval: number): Date {
    const minutes = Math.round(time.getMinutes() / interval) * interval;
    time.setMinutes(minutes);
    time.setSeconds(0);
    time.setMilliseconds(0);
    return time;
  }

  function formatTime(time: Date): string {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const hh = hours < 10 ? '0' + hours : hours;
    const mm = minutes < 10 ? '0' + minutes : minutes;
    return `${hh}:${mm}`;
  }

  function formatAmPm(time: Date): string {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const hh = hours % 12 || 12;
    const mm = minutes < 10 ? '0' + minutes : minutes;
    const amPm = hours < 12 ? 'am' : 'pm';
    return `${hh}:${mm}${amPm}`;
  }

  const startDate = new Date(`1970-01-01T${startTime}:00`);
  const endDate = new Date(`1970-01-01T${endTime}:00`);

  const roundedStartDate = roundToNearestInterval(startDate, interval);
  const timeOptions: TimeOption[] = [];

  while (roundedStartDate <= endDate) {
    timeOptions.push({
      text: formatAmPm(roundedStartDate),
      value: formatTime(roundedStartDate),
    });
    roundedStartDate.setMinutes(roundedStartDate.getMinutes() + interval);
  }
  return sortTimeIncrements(timeOptions);
}

type Props = {
  label?: string;
  timeLabel?: string;
  value?: Dayjs;
  onChange: (val: Dayjs) => void;
  errors?: FormError;
  timezone: string;
};

export const DateTime: React.FC<Props> = ({
  label = 'Date',
  timeLabel = 'Time',
  value,
  onChange,
  errors,
  timezone,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const localTimeValue = useMemo(() => {
    return isDayjs(value)
      ? value?.format('HH:mm')
      : dayjs(value).format('HH:mm');
  }, [value]);

  const theDate = useMemo(() => {
    const date = isDayjs(value)
      ? value?.format('YYYY-MM-DD')
      : dayjs(value).format('YYYY-MM-DD');
    return { startDate: date, endDate: date };
  }, [value]);

  const options = useMemo(() => {
    return generateTimeOptions('00:00', '23:45', 15);
  }, []);

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.text.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, options]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col">
        <label
          htmlFor="date"
          className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2 dark:text-darkGray-200"
        >
          {label}
        </label>
        <div id="date">
          <Datepicker
            value={theDate as any}
            onChange={(val: any) => {
              try {
                onChange(
                  dayjs.tz(`${val.startDate} ${localTimeValue}`, timezone),
                );
              } catch (e: any) {
                // Noop: Just don't want to kill the interpreter.
              }
            }}
            asSingle
            useRange={false}
            placeholder={'MM/DD/YYYY'}
            displayFormat={'MM/DD/YYYY'}
            primaryColor={'green'}
            inputClassName="border border-gray-300 shadow-sm rounded-md mt-1 focus:outline-none focus:ring-none focus:ring-primary-500 focus:border-transparent pl-2 dark:bg-darkGray-600 dark:text-darkGray-200"
            readOnly={true}
          />
        </div>
      </div>
      <Autocomplete
        searchTerm={searchTerm}
        onChangeSearch={setSearchTerm}
        name="startTime"
        label={timeLabel}
        value={localTimeValue}
        onChange={(val: string) => {
          try {
            onChange(dayjs.tz(`${theDate.startDate} ${val}`, timezone));
          } catch (e: any) {
            // Noop: Just don't want to kill the interpreter.
          }
        }}
        options={filteredOptions}
        limit={1}
      />
      <FieldErrors errors={errors} />
    </div>
  );
};
